import moment from 'moment'
import { ContactJson } from 'app/types/entities'

/**
 * The facts a board card renders beyond name and play. Pure, and returning
 * strings rather than JSX, so the wording has coverage without mounting a board.
 */
export type BoardCardFacts = {
  appointment: { label: string; tone: `green` | `amber` | `gray` }
  lastTouch: string
  details: string[]
  /** Set for leads (how long they have waited); hygiene's figure comes from `playDetail` instead. */
  age?: string
}

export type UrgencyTone = `calm` | `soon` | `late`

/**
 * Where amber becomes red: the same 90 days at which `playDetail` switches
 * from counting days to counting months, so the unit and the colour change
 * together.
 */
const SOON_AFTER_DUE_DAYS = 90

/**
 * UTC, like every other date-only rollup the Action Center reads: parsed
 * locally, a date stored at midnight UTC lands a day early for anyone west
 * of it.
 */
export function ago(value?: string | null, now: moment.Moment = moment.utc()): string | undefined {
  if (!value) return undefined
  const then = moment.utc(value)
  if (!then.isValid()) return undefined
  const days = now.clone().startOf(`day`).diff(then.clone().startOf(`day`), `days`)
  if (days < 0) return undefined
  if (days === 0) return `today`
  if (days === 1) return `yesterday`
  if (days < 28) return `${days}d ago`
  const months = now.clone().startOf(`day`).diff(then.clone().startOf(`day`), `months`)
  if (months < 24) return `${Math.max(1, months)}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

/** UTC, see `ago`. */
export function onDate(value?: string | null, now: moment.Moment = moment.utc()): string | undefined {
  if (!value) return undefined
  const when = moment.utc(value)
  if (!when.isValid()) return undefined
  return when.year() === now.year() ? when.format(`MMM D`) : when.format(`MMM D, YYYY`)
}

/** "(not set)" is a real stored value and reads as noise, so it is treated as absent. */
export function sourceLabel(contact: ContactJson): string | undefined {
  const candidates = [contact.utmSource, contact.channel, contact.platform]
  for (const candidate of candidates) {
    const value = candidate?.trim()
    if (value && !/^\(?not set\)?$/i.test(value)) return value
  }
  return undefined
}

export function ageFrom(birthDate?: string | null, now: moment.Moment = moment.utc()): number | undefined {
  if (!birthDate) return undefined
  const born = moment.utc(birthDate)
  if (!born.isValid()) return undefined
  const years = now.diff(born, `years`)
  return years >= 0 && years < 130 ? years : undefined
}

/**
 * `nearestAppointmentDate` holds the nearest appointment whatever became of it
 * (a broken one included), so the date alone is not an answer;
 * `appointmentStatus` says which it is.
 */
export function appointmentFact(
  contact: ContactJson,
  now: moment.Moment = moment.utc(),
): { label: string; tone: `green` | `amber` | `gray` } {
  const status = contact.appointmentStatus?.trim()
  const when = onDate(contact.nearestAppointmentDate, now)
  const at = contact.nearestAppointmentDate ? moment.utc(contact.nearestAppointmentDate) : undefined
  const ahead = !!at?.isValid() && !at.isBefore(now.clone().startOf(`day`))

  // Kolla reports an appointment removed from the schedule as Deleted. It
  // never happened, so it is neither booked nor broken, whatever its date.
  if (status && /delete/i.test(status)) return { label: `Nothing booked`, tone: `gray` }

  // Broken and cancelled next: a broken appointment can still be in the
  // future, and the date alone cannot tell the difference.
  if (status && /cancel|broke/i.test(status)) {
    const verb = /cancel/i.test(status) ? `Cancelled` : `Broken`
    return { label: when ? `${verb} ${when}` : verb, tone: `amber` }
  }

  // The date, not the status: `appointmentStatus` is not reliably set on
  // records that do hold a future appointment.
  if (ahead && when) return { label: `Booked ${when}`, tone: `green` }

  // `Completed` included: a visit they attended is not a warning.
  return { label: `Nothing booked`, tone: `gray` }
}

/**
 * `lastCommDate` only: `lastActivityDate` counts system plumbing (stage
 * changes, "Patient associated"), which would report contact where none
 * happened.
 */
export function touchLabel(contact: ContactJson, now: moment.Moment = moment.utc()): string {
  const when = ago(contact.lastCommDate, now)
  // "No communication logged", not "Never contacted": the card can only see
  // what was written down.
  return when ? `Last contact ${when}` : `No communication logged`
}

/** Under a fortnight reads as an active conversation; older is a stale attempt. */
export function isInConversation(contact: ContactJson, now: moment.Moment = moment.utc()): boolean {
  if (!contact.lastCommDate) return false
  return now.clone().startOf(`day`).diff(moment.utc(contact.lastCommDate).startOf(`day`), `days`) < 14
}

function hygieneDetails(contact: ContactJson, now: moment.Moment): string[] {
  const seen = onDate(contact.lastHygieneDate ?? contact.lastAppointmentDate, now)
  return seen ? [`Last cleaning ${seen}`] : []
}

function leadDetails(contact: ContactJson, now: moment.Moment): string[] {
  // The age is the figure opposite the name, so this line carries only where
  // they came from.
  return [sourceLabel(contact)].filter((fact): fact is string => !!fact)
}

/**
 * Days, not months: `hygieneMonthsOverdue` clamps under a month, which is the
 * whole width of the board's first overdue band.
 */
export function urgencyTone(daysUntilDue: number | undefined, fallback: UrgencyTone = `calm`): UrgencyTone {
  if (daysUntilDue == null) return fallback
  if (daysUntilDue > 0) return `calm`
  return -daysUntilDue <= SOON_AFTER_DUE_DAYS ? `soon` : `late`
}

/**
 * Deliberately not calmed by `lastCommDate`: it counts the AI agent's own
 * replies, and an automated text is not somebody working the lead.
 */
export function leadUrgencyTone(contact: ContactJson, now: moment.Moment = moment.utc()): UrgencyTone {
  const arrived = contact.becameLeadDate ?? contact.createdDate
  if (!arrived) return `soon`
  const days = now.clone().startOf(`day`).diff(moment.utc(arrived).startOf(`day`), `days`)
  if (days <= 0) return `calm`
  return days <= 3 ? `soon` : `late`
}

/** Tags about our own data rather than the patient. */
const PLUMBING_TAGS = [`No Patient Found`, `No Patient Linked`, `Invalid Note`]

/**
 * Tags the appointment chip already answers with a date attached. Suppressed
 * only while the chip is drawn (`appointmentShown`): for some contacts they
 * are the only tag, and the only thing saying why the person is on the board.
 */
const APPOINTMENT_TAGS = [`Broken Appointment`, `Canceled Appointment`, `No Appointment`, `Appointment Not Scheduled`]

export function cardTags(
  tags: string[] | undefined | null,
  { appointmentShown = true, limit = 3 }: { appointmentShown?: boolean; limit?: number } = {},
): { shown: string[]; extra: number } {
  const worth = (tags ?? []).filter(
    (tag) => tag && !PLUMBING_TAGS.includes(tag) && !(appointmentShown && APPOINTMENT_TAGS.includes(tag)),
  )
  return { shown: worth.slice(0, limit), extra: Math.max(0, worth.length - limit) }
}

/**
 * A lead whose only contact is the event that made them a lead reads "No
 * follow-up logged". Compared by day, not timestamp: the lead record and the
 * call that created it are written seconds apart.
 */
function arrivalIsTheOnlyContact(contact: ContactJson, family: `lead` | `patient` | undefined): boolean {
  if (family !== `lead`) return false
  const arrived = contact.becameLeadDate ?? contact.createdDate
  const touched = contact.lastCommDate
  if (!arrived || !touched) return false
  return !moment.utc(touched).startOf(`day`).isAfter(moment.utc(arrived).startOf(`day`))
}

export function boardCardFacts(
  contact: ContactJson,
  family: `lead` | `patient` | undefined,
  now: moment.Moment = moment.utc(),
): BoardCardFacts {
  return {
    appointment: appointmentFact(contact, now),
    lastTouch: arrivalIsTheOnlyContact(contact, family) ? `No follow-up logged` : touchLabel(contact, now),
    details: family === `lead` ? leadDetails(contact, now) : hygieneDetails(contact, now),
    age: family === `lead` ? ago(contact.becameLeadDate ?? contact.createdDate, now) : undefined,
  }
}
